/**
 * Timezone-aware date utilities
 * CRITICAL: Fixes date grouping/filtering discrepancies by enforcing Asia/Ho_Chi_Minh timezone
 */

import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

export const APP_TIMEZONE = 'Asia/Ho_Chi_Minh';

/**
 * Convert Date or date string to dateKey (YYYY-MM-DD) in APP_TIMEZONE
 * IDEMPOTENT: Running multiple times produces same result
 * 
 * @param input - Date object or YYYY-MM-DD string
 * @returns Normalized dateKey (YYYY-MM-DD)
 * 
 * @example
 * toDateKey('2025-01-10') // '2025-01-10' (no conversion)
 * toDateKey(new Date('2025-01-10T23:00:00Z')) // '2025-01-11' (Asia/Ho_Chi_Minh)
 */
export function toDateKey(input: Date | string): string {
  // If already YYYY-MM-DD string, return as-is (avoid UTC shift from new Date())
  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    return input;
  }
  
  // If Date object, convert to APP_TIMEZONE then format
  const zoned = toZonedTime(input as Date, APP_TIMEZONE);
  return format(zoned, 'yyyy-MM-dd');
}

/**
 * Compare two dateKeys (YYYY-MM-DD strings)
 * @returns -1 if a < b, 0 if a === b, 1 if a > b
 */
export function compareDateKeys(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/**
 * Check if dateKey is within range (inclusive)
 * @param dateKey - Date string in YYYY-MM-DD format
 * @param from - Start date (optional)
 * @param to - End date (optional)
 * @returns true if dateKey is within [from, to]
 */
export function isDateInRange(
  dateKey: string,
  from?: Date,
  to?: Date
): boolean {
  if (!from && !to) return true;
  
  const fromKey = from ? toDateKey(from) : null;
  const toKey = to ? toDateKey(to) : null;
  
  if (fromKey && dateKey < fromKey) return false;
  if (toKey && dateKey > toKey) return false;
  return true;
}

/**
 * Format a date string or Date object to display format in APP_TIMEZONE
 * 
 * @param input - Date object or date string (YYYY-MM-DD or ISO)
 * @param formatString - date-fns format string (default: 'dd/MM/yyyy')
 * @returns Formatted date string in APP_TIMEZONE
 * 
 * @example
 * formatInAppTimezone('2025-01-10', 'dd/MM/yyyy') // '10/01/2025'
 * formatInAppTimezone(new Date('2025-01-10T23:00:00Z'), 'dd/MM/yyyy') // '11/01/2025' (Asia/Ho_Chi_Minh)
 */
export function formatInAppTimezone(
  input: Date | string | null | undefined,
  formatString: string = 'dd/MM/yyyy'
): string {
  if (!input) return '-';
  
  try {
    // Convert string to Date if needed
    const date = typeof input === 'string' ? new Date(input) : input;
    
    // Convert to APP_TIMEZONE and format
    const zoned = toZonedTime(date, APP_TIMEZONE);
    return format(zoned, formatString);
  } catch (error) {
    console.error('Error formatting date:', error, input);
    return '-';
  }
}

/**
 * Convert datetime-local input value (local Vietnam time) to ISO string (UTC)
 * 
 * @param localDatetime - datetime-local input value (e.g., "2025-12-25T23:59")
 * @returns ISO string in UTC (e.g., "2025-12-25T16:59:00.000Z")
 * 
 * @example
 * localDatetimeToUTC("2025-12-25T23:59") // "2025-12-25T16:59:00.000Z"
 */
export function localDatetimeToUTC(localDatetime: string): string {
  if (!localDatetime) return '';
  
  // Parse as Vietnam local time by appending timezone offset
  // Vietnam is UTC+7, so we subtract 7 hours to get UTC
  const [datePart, timePart] = localDatetime.split('T');
  const localDate = new Date(`${datePart}T${timePart}:00+07:00`);
  
  return localDate.toISOString();
}

/**
 * Convert UTC/ISO datetime from database to datetime-local format for input
 * 
 * @param utcDatetime - UTC datetime from database (e.g., "2025-12-25T16:59:00+00")
 * @returns datetime-local format string in Vietnam time (e.g., "2025-12-25T23:59")
 * 
 * @example
 * utcToLocalDatetime("2025-12-25T16:59:00+00") // "2025-12-25T23:59"
 */
export function utcToLocalDatetime(utcDatetime: string | null | undefined): string {
  if (!utcDatetime) return '';
  
  try {
    const date = new Date(utcDatetime);
    // Convert to Vietnam timezone
    const zoned = toZonedTime(date, APP_TIMEZONE);
    return format(zoned, "yyyy-MM-dd'T'HH:mm");
  } catch (error) {
    console.error('Error converting UTC to local datetime:', error);
    return '';
  }
}

/**
 * Safe date parsing for legacy migration
 * @deprecated Use toDateKey() instead
 */
export function parseDateSafe(dateString: string): string {
  return toDateKey(dateString);
}
